/**
 * Deterministický RNG (mulberry32). Stav hry nesie `seed` + `rngCursor`,
 * takže server aj klient vedia prehrať presne tú istú partiu.
 *
 * Nikdy nepoužívaj Math.random() v engine — replay by sa rozišiel.
 */

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface RngHolder {
  seed: number;
  rngCursor: number;
}

/**
 * Prúd pri danom kurzore = seed pretočený o `rngCursor` hodnôt. Pretáčanie je
 * O(n), preto si posledný generátor držíme v cache — bežné sekvenčné volanie
 * je tak O(1) a determinizmus zostáva zachovaný (cache je len zrýchlenie).
 */
let cached: { seed: number; cursor: number; gen: () => number } | null = null;

function generatorAt(seed: number, cursor: number): () => number {
  if (cached && cached.seed === seed && cached.cursor === cursor) return cached.gen;
  const gen = mulberry32(seed);
  for (let i = 0; i < cursor; i++) gen();
  cached = { seed, cursor, gen };
  return gen;
}

/**
 * Vytiahne ďalšie náhodné číslo z prúdu a posunie kurzor.
 * Prúd sa pretáča od začiatku — pri kurzore N sa preskočí N hodnôt.
 */
export function nextRandom(holder: RngHolder): number {
  const gen = generatorAt(holder.seed, holder.rngCursor);
  const value = gen();
  holder.rngCursor++;
  cached = { seed: holder.seed, cursor: holder.rngCursor, gen };
  return value;
}

/** Celé číslo z intervalu [0, max). */
export function nextInt(holder: RngHolder, max: number): number {
  return Math.floor(nextRandom(holder) * max);
}

/** Fisher-Yates nad kópiou poľa, deterministicky podľa kurzora. */
export function shuffle<T>(holder: RngHolder, input: readonly T[]): T[] {
  const out = input.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = nextInt(holder, i + 1);
    const a = out[i] as T;
    const b = out[j] as T;
    out[i] = b;
    out[j] = a;
  }
  return out;
}

/** Náhodný seed pre novú hru (jediné miesto, kde smie byť Math.random). */
export function createSeed(): number {
  return Math.floor(Math.random() * 0xffffffff) >>> 0;
}
